import { getSubjectLabels } from '@/lib/settings';
import React, { useState, useRef, useEffect } from 'react';
import { addDays, format } from 'date-fns';
import { toast } from 'sonner';
import { Button } from '../../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Input } from '../../../../components/ui/input';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Badge } from '../../../../components/ui/badge';
import { lehrerApi } from '../../../../lib/unterrichtseinheiten';
import {
  masterdataApi,
  personSearchApi,
  type UnterrichtsformItem,
  type FachItem,
  type PersonSuggestion,
} from '../../../../lib/courses';
import { getRooms } from '../../../../lib/rooms';
import { api } from '../../../../lib/api';

interface CreateCourseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedTeacher: string | number | undefined;
  teacherLabel: string;
  selectedStandortId: string | number | null;
  currentWeek: Date;
  /** The item from the context menu that triggered the dialog (provides hour, dayOfWeek, etc.) */
  selectedItem: { dayOfWeek?: number; hour?: string; teacherId?: string | number } | null;
  onCreated: () => void;
}

interface CreateCourseForm {
  unterrichtsformId?: number;
  fachId?: number;
  raumId?: number;
  selectedSubprofiles?: { id: string; name: string; personId: number }[];
  schuelerQuery?: string;
  schuelerOptions?: PersonSuggestion[];
}

interface CreateCourseData {
  unterrichtsformen: UnterrichtsformItem[];
  faecher: FachItem[];
  availableFaecher: FachItem[];
  raeume: Array<{ id: number; name: string }>;
}

const CreateCourseDialog: React.FC<CreateCourseDialogProps> = ({
  open,
  onOpenChange,
  selectedTeacher,
  teacherLabel,
  selectedStandortId,
  currentWeek,
  selectedItem,
  onCreated,
}) => {
  const [createCourseData, setCreateCourseData] = useState<CreateCourseData>({
    unterrichtsformen: [],
    faecher: [],
    availableFaecher: [],
    raeume: [],
  });
  const [form, setForm] = useState<CreateCourseForm>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // T-0188: Raum in den nächsten 12 Wochen schon belegt? Warnen, mit Bestätigung anlegen.
  const [raumKonflikte, setRaumKonflikte] = useState<
    { date: string; startTime: string; endTime: string; kursName: string | null }[] | null
  >(null);
  useEffect(() => {
    setRaumKonflikte(null);
  }, [open, form.raumId]);
  const startDateRef = useRef<HTMLInputElement>(null);
  const timeStartRef = useRef<HTMLInputElement>(null);
  const untilDateRef = useRef<HTMLInputElement>(null);
  const selectedDayOffset = Math.min(6, Math.max(0, Number(selectedItem?.dayOfWeek || 1) - 1));
  const defaultStartDate = format(addDays(currentWeek, selectedDayOffset), 'yyyy-MM-dd');

  // Load master data when dialog opens
  useEffect(() => {
    if (!open) return;
    const loadData = async () => {
      try {
        const [uf, f, rooms] = await Promise.all([
          masterdataApi.unterrichtsformen(),
          masterdataApi.faecher(
            selectedStandortId == null ? undefined : Number(selectedStandortId),
          ),
          getRooms(),
        ]);
        const raeume = rooms.map((r) => ({ id: r.id, name: r.name }));

        let availableFaecher: FachItem[] = [];
        if (selectedTeacher) {
          try {
            const teacherFaecher = await lehrerApi.getAvailableFaecher(Number(selectedTeacher));
            const allowedIds = new Set(f.map((item) => Number(item.id)));
            availableFaecher = (teacherFaecher || []).filter((item) => allowedIds.has(Number(item.id)));
          } catch (e) {
            console.error('Fehler beim Laden verfuegbarer Faecher:', e);
            availableFaecher = f;
          }
        } else {
          availableFaecher = f;
        }

        setCreateCourseData({ unterrichtsformen: uf, faecher: f, availableFaecher, raeume });
      } catch {
        setCreateCourseData({ unterrichtsformen: [], faecher: [], availableFaecher: [], raeume: [] });
      }
    };
    loadData();
  }, [open, selectedTeacher, selectedStandortId]);

  const handleSubmit = async (trotzRaumkonflikt = false) => {
    if (isSubmitting) return;
    try {
      if (!selectedTeacher) {
        toast.error('Bitte zuerst einen ' + teacherLabel + ' auswaehlen');
        return;
      }
      if (!form.unterrichtsformId || !form.fachId || !form.selectedSubprofiles || form.selectedSubprofiles.length === 0) {
        toast.error(`Unterrichtsform, ${getSubjectLabels().singular} und mindestens ein Schülerprofil sind erforderlich`);
        return;
      }
      const startDate = startDateRef.current?.value || defaultStartDate;
      const timeStart = timeStartRef.current?.value || '09:00';
      const untilDate = untilDateRef.current?.value || '';

      const uf = createCourseData.unterrichtsformen.find((u) => u.id === form.unterrichtsformId);
      const dauerMin = uf?.dauer ?? 60;

      if (!selectedStandortId) {
        toast.error('Bitte zuerst einen Standort auswaehlen');
        return;
      }
      if (untilDate && untilDate < startDate) {
        toast.error('Das Enddatum darf nicht vor dem Startdatum liegen');
        return;
      }
      if (form.raumId && !trotzRaumkonflikt) {
        const [h, m] = timeStart.split(':').map(Number);
        const ende = (h || 0) * 60 + (m || 0) + dauerMin;
        const endTime = `${String(Math.floor(ende / 60)).padStart(2, '0')}:${String(ende % 60).padStart(2, '0')}`;
        try {
          const pruefung = await api.post<{ conflicts: NonNullable<typeof raumKonflikte> }>(
            '/api/schedule/room-series-conflicts',
            { roomId: form.raumId, startDate, startTime: timeStart, endTime, intervalWeeks: 1, weeks: 12 },
          );
          const gefunden = (pruefung?.conflicts || []).filter((k) => !untilDate || k.date <= untilDate);
          if (gefunden.length > 0) {
            setRaumKonflikte(gefunden);
            return;
          }
        } catch (e) {
          // Die Prüfung soll das Anlegen nicht verhindern, wenn sie selbst scheitert.
          console.error('Raumprüfung fehlgeschlagen:', e);
        }
      }
      setIsSubmitting(true);

      let defaultOnlineMeetingUrl: string | null = null;
      try {
        const cfg = await api.get<{ enabled?: boolean; defaultMeetingUrl?: string }>('/api/settings/online-course');
        const enabled = !!cfg?.enabled;
        const url = (cfg?.defaultMeetingUrl || '').trim();
        if (enabled && url) defaultOnlineMeetingUrl = url;
      } catch {
        // ignore (feature optional)
      }

      const fach = createCourseData.faecher.find((item) => item.id === form.fachId);
      const participantNames = form.selectedSubprofiles.map((item) => item.name).join(', ');
      const startDow = new Date(startDate).getDay();
      const dow = ((startDow + 6) % 7) + 1;
      const timeEnd = (() => {
        const start = new Date(`1970-01-01T${timeStart}:00Z`);
        const end = new Date(start.getTime() + dauerMin * 60000);
        const hh = String(end.getUTCHours()).padStart(2, '0');
        const mm = String(end.getUTCMinutes()).padStart(2, '0');
        return `${hh}:${mm}:00`;
      })();
      // Kurs, Regel und Teilnahmen in einem Aufruf — der Server legt alles oder
      // nichts an (T-0181). Vorher drei Aufrufe mit Aufräumen im Browser.
      await api.post('/api/kurse/mit-regel', {
        kurs: {
          name: `${fach?.name || 'Kurs'} – ${participantNames}`,
          fach_id: form.fachId,
          unterrichtsform_id: form.unterrichtsformId,
          default_lehrer_id: Number(selectedTeacher),
          default_raum_id: form.raumId || null,
          standort_id: selectedStandortId,
          dauer_min: dauerMin,
          online_meeting_url: defaultOnlineMeetingUrl,
          is_active: true,
        },
        regel: {
          start_datum: startDate,
          time_start: timeStart + ':00',
          time_end: timeEnd,
          freq: 'WEEKLY',
          intervall: 1,
          byweekday: String(dow),
          until: untilDate || null,
          aktiv: true,
        },
        teilnehmer: (form.selectedSubprofiles || []).map((s) => ({
          person_id: s.personId,
          subprofile_id: s.id,
          status: 'active',
        })),
      });

      onOpenChange(false);
      setForm({});
      onCreated();
      toast.success(`Kurs erfolgreich angelegt${form.raumId ? ' (Raum zugewiesen)' : ''}`);
    } catch (e: unknown) {
      console.error('Fehler beim Anlegen des Kurses:', e);
      toast.error(e instanceof Error ? e.message : 'Kurs konnte nicht angelegt werden');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!selectedItem) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(openVal) => {
        onOpenChange(openVal);
        if (!openVal) {
          setForm({});
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Neuen Kurs anlegen</DialogTitle>
          <DialogDescription>Unterrichtsform, {getSubjectLabels().singular} und mindestens ein Schülerprofil auswählen.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {/* Unterrichtsform */}
          <div className="grid grid-cols-4 items-center gap-4">
            <label className="text-right text-sm font-medium">Unterrichtsform</label>
            <Select
              value={form.unterrichtsformId ? String(form.unterrichtsformId) : ''}
              onValueChange={(val) => setForm((prev) => ({ ...prev, unterrichtsformId: Number(val) }))}
            >
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder="Unterrichtsform auswaehlen" />
              </SelectTrigger>
              <SelectContent>
                {createCourseData.unterrichtsformen.map((uf) => (
                  <SelectItem key={uf.id} value={String(uf.id)}>
                    {uf.name} ({uf.dauer} Min){typeof uf.max_schueler === 'number' ? ` · max ${uf.max_schueler}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Fach */}
          <div className="grid grid-cols-4 items-center gap-4">
            <label className="text-right text-sm font-medium">{getSubjectLabels().singular}</label>
            <Select
              value={form.fachId ? String(form.fachId) : ''}
              onValueChange={(val) => setForm((prev) => ({ ...prev, fachId: Number(val) }))}
            >
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder={`${getSubjectLabels().singular} auswählen`} />
              </SelectTrigger>
              <SelectContent>
                {(createCourseData.availableFaecher.length > 0 ? createCourseData.availableFaecher : createCourseData.faecher).map(
                  (f) => (
                    <SelectItem key={f.id} value={String(f.id)}>
                      {f.name}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
            {selectedTeacher && createCourseData.availableFaecher.length === 0 && (
              <p className="col-span-3 text-xs text-muted-foreground">Keine verfügbaren {getSubjectLabels().plural} für diesen Lehrer gefunden</p>
            )}
          </div>
          {/* Raum */}
          <div className="grid grid-cols-4 items-center gap-4">
            <label className="text-right text-sm font-medium">Raum (optional)</label>
            <Select
              value={form.raumId ? String(form.raumId) : 'none'}
              onValueChange={(val) => setForm((prev) => ({ ...prev, raumId: val === 'none' ? undefined : Number(val) }))}
            >
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder="Raum auswaehlen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Kein Raum</SelectItem>
                {createCourseData.raeume.map((r) => (
                  <SelectItem key={r.id} value={String(r.id)}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Teilnehmer (Subprofile) */}
          <div className="grid grid-cols-4 items-start gap-4">
            <label className="text-right text-sm font-medium pt-2">Teilnehmer</label>
            <div className="col-span-3 space-y-2">
              <Input
                placeholder="Name/E-Mail suchen"
                value={form.schuelerQuery || ''}
                onChange={async (e) => {
                  const q = e.target.value;
                  setForm((prev) => ({ ...prev, schuelerQuery: q }));
                  if (q.length < 2) {
                    setForm((prev) => ({ ...prev, schuelerOptions: [] }));
                    return;
                  }
                  try {
                    const res = await personSearchApi.search(q, 'subprofile', 20);
                    const options = (res || []).filter((p) => p.type === 'subprofile');
                    setForm((prev) => ({ ...prev, schuelerOptions: options }));
                  } catch {
                    // Ignore transient search errors while typing.
                  }
                }}
              />

              {(form.schuelerOptions || []).length > 0 && (
                <div className="border rounded-md max-h-60 overflow-auto p-2">
                  {(form.schuelerOptions || []).map((p) => {
                    const selected = (form.selectedSubprofiles || []).some((s) => s.id === String(p.id));
                    const ufItem = createCourseData.unterrichtsformen.find((u) => u.id === form.unterrichtsformId);
                    const max = ufItem?.max_schueler ?? 1;
                    const currentCount = (form.selectedSubprofiles || []).length;
                    const isMaxReached = currentCount >= max && !selected;

                    return (
                      <div key={p.id} className="flex items-center gap-3 py-2 px-2 hover:bg-accent rounded-md">
                        <Checkbox
                          checked={selected}
                          disabled={isMaxReached}
                          onCheckedChange={(checked) => {
                            setForm((prev) => {
                              const current = prev.selectedSubprofiles || [];
                              if (checked) {
                                if (current.length >= max) {
                                  toast.error(`Maximale Teilnehmerzahl (${max}) erreicht`);
                                  return prev;
                                }
                                const personId = Number(p.parentId);
                                if (!Number.isInteger(personId) || personId <= 0) {
                                  toast.error('Das Lernendenprofil ist keiner Person zugeordnet');
                                  return prev;
                                }
                                return {
                                  ...prev,
                                  selectedSubprofiles: [
                                    ...current,
                                    { id: String(p.id), name: p.name, personId },
                                  ],
                                };
                              } else {
                                return { ...prev, selectedSubprofiles: current.filter((s) => s.id !== String(p.id)) };
                              }
                            });
                          }}
                        />
                        <label className="text-sm flex-1 cursor-pointer">
                          {p.name}
                          {p.parentId && <span className="text-xs text-muted-foreground ml-2">(ID: {p.id})</span>}
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}

              {form.selectedSubprofiles && form.selectedSubprofiles.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Ausgewaehlte Teilnehmer:</p>
                  <div className="flex flex-wrap gap-2">
                    {form.selectedSubprofiles.map((s) => (
                      <Badge key={s.id} variant="secondary" className="inline-flex items-center gap-1 px-3 py-1 text-sm">
                        {s.name}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 hover:text-primary/70"
                          aria-label={`${s.name} entfernen`}
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              selectedSubprofiles: (prev.selectedSubprofiles || []).filter((x) => x.id !== s.id),
                            }))
                          }
                        >
                          x
                        </Button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <label className="text-right text-sm font-medium">Startdatum</label>
            <Input
              key={defaultStartDate}
              ref={startDateRef}
              type="date"
              className="col-span-3"
              defaultValue={defaultStartDate}
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <label className="text-right text-sm font-medium">Beginn</label>
            <Input ref={timeStartRef} type="time" className="col-span-3" defaultValue={selectedItem.hour || '09:00'} />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <label className="text-right text-sm font-medium">Enddatum (optional)</label>
            <Input ref={untilDateRef} type="date" className="col-span-3" placeholder="Leer = unbegrenzt" />
          </div>
        </div>
        {raumKonflikte && raumKonflikte.length > 0 ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
            <p className="font-medium">
              Der Raum ist in den nächsten 12 Wochen an {raumKonflikte.length}{' '}
              {raumKonflikte.length === 1 ? 'Termin' : 'Terminen'} schon belegt:
            </p>
            <ul className="mt-1 list-disc pl-5">
              {raumKonflikte.slice(0, 5).map((k) => (
                <li key={`${k.date}-${k.startTime}-${k.kursName}`}>
                  {format(new Date(`${k.date}T00:00:00`), 'dd.MM.yyyy')} {k.startTime}–{k.endTime}
                  {k.kursName ? ` · ${k.kursName}` : ''}
                </li>
              ))}
              {raumKonflikte.length > 5 ? <li>… und {raumKonflikte.length - 5} weitere</li> : null}
            </ul>
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Abbrechen
          </Button>
          {raumKonflikte && raumKonflikte.length > 0 ? (
            <Button variant="destructive" onClick={() => void handleSubmit(true)} disabled={isSubmitting}>
              {isSubmitting ? 'Wird angelegt…' : 'Trotzdem anlegen'}
            </Button>
          ) : (
            <Button onClick={() => void handleSubmit()} disabled={isSubmitting}>
              {isSubmitting ? 'Wird angelegt…' : 'Erstellen'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateCourseDialog;
